import { defineStore } from 'pinia';
import { set, get, del } from 'idb-keyval';
import { ApiClient } from '../lib/apiClient';
import { useApi } from '../composables/useApi.ts';
import { sessionHeaders } from '../composables/useApiClient.ts';
import evidenceTypes from '../constants/evidence-types.ts'

const api = useApi();
const apiClient = new ApiClient();

export const LOGOUT_TIMEOUT_MS = 5_000;

export const useUserStore = defineStore('user', {
    state: () => {
        return {
            name: null as string | null,
            accessToken: null as string | null,
            id: null as string | null,
            person: null as object | null,
            parent: null as string | null,
            serviceParent: null as string | null,
            userLogin: null as string | null,
            boss: {},
        };
    },
    getters: {
        isLoggedIn: async (state) => {
            //when browser refreshes fill the state from indexedDB
            if (!state.accessToken) {
                const user = await get('user');
                if (user) {
                    state.accessToken = user.accessToken;
                    state.id = user.id;
                    state.name = user.name;
                    state.person = user.person;
                    state.parent = user.parent;
                    state.serviceParent = user.serviceParent;
                    state.userLogin = user.userLogin;
                }
            }
            return !!state.accessToken;
        },
        //TODO FIX
        isAuthenticated: (state) => {
            if (!state.accessToken) {
                const user = get('user');
                if (user) {
                    state.accessToken = user.accessToken;
                    state.id = user.id;
                    state.name = user.name;
                }
            }
            return !!state.accessToken;
        },
        isVatPayer () {
            return this.boss?.company?.vat_payer ? !!this.boss.company.vat_payer : undefined
        },
        isKpir() {
            return evidenceTypes.EVIDENCE_TYPE_KPIR === this.boss.company.form_of_accounting.id
        },
        isRyczalt() {
            return evidenceTypes.EVIDENCE_TYPE_RYCZALT === this.boss.company.form_of_accounting.id
        },
        isOfTypeKh() {
            return evidenceTypes.EVIDENCE_TYPE_KH === this.boss.company.form_of_accounting.id
        },
    },
    actions: {
        async login(email: string, password: string) {
            const response = await apiClient.post('/login', {
                login: email.trim(),
                password: password,
            });

            if (response.status === 422) {
                throw new InvalidCredentialsError();
            }

            if (response.status === 401) {
                throw new InvalidCredentialsError();
            }

            if (response.status !== 200) {
                const { message, reason } = await getGatewayError(response);
                throw new GatewayMessageError(message ?? '', { status: response.status, reason });
            }

            let body: any;
            try {
                body = await response.json();
            } catch {
                throw new NetworkError();
            }

            // A hub account must enrol first; the challenge stays with the login page, never in the store.
            if (body?.twoFactorSetupRequired === true) {
                if (!body.challenge) {
                    throw new NetworkError();
                }

                return {
                    twoFactorRequired: false as const,
                    twoFactorSetupRequired: true as const,
                    challenge: body.challenge as string,
                    expiresIn: Number(body.expiresIn) || null,
                };
            }

            try {
                if (body?.twoFactorRequired === true) {
                    if (!body.challenge || !['email', 'totp'].includes(body.method)) {
                        throw new NetworkError();
                    }

                    return {
                        twoFactorRequired: true as const,
                        challenge: body.challenge as string,
                        method: body.method as 'email' | 'totp',
                        expiresIn: Number(body.expiresIn) || null,
                    };
                }

                await this.persistLogin(body);
            } catch (error) {
                if (error instanceof NetworkError) throw error;
                console.error('userStore.login', error);
                throw new DatabaseError();
            }

            return { twoFactorRequired: false as const };
        },
        async verifyTwoFactor(challenge: string, code: string, recovery = false) {
            const response = await postTwoFactor('/login/two-factor/verify', {
                challenge,
                code,
                recovery,
            });

            try {
                const body = await response.json();
                await this.persistLogin(body);
            } catch (error) {
                if (error instanceof NetworkError) throw error;
                console.error('userStore.verifyTwoFactor', error);
                throw new DatabaseError();
            }

            return true;
        },
        async resendTwoFactorCode(challenge: string) {
            const response = await postTwoFactor('/login/two-factor/resend', { challenge });

            const body = await readBody(response);
            if (
                body?.twoFactorRequired !== true
                || body.challenge !== challenge
                || !['email', 'totp'].includes(body.method)
            ) {
                throw new NetworkError();
            }

            return {
                twoFactorRequired: true as const,
                challenge: body.challenge as string,
                method: body.method as 'email' | 'totp',
                expiresIn: Number(body.expiresIn) || null,
            };
        },
        async startLoginTwoFactorSetup(challenge: string, method: 'email' | 'totp') {
            const response = await postTwoFactor('/login/two-factor/setup', { challenge, method });

            const body = await readBody(response);
            if (!['email', 'totp'].includes(body?.method)) {
                throw new NetworkError();
            }

            return {
                method: body.method as 'email' | 'totp',
                secret: (body.secret || null) as string | null,
                otpauthUri: (body.otpauthUri || null) as string | null,
                expiresIn: Number(body.expiresIn) || null,
            };
        },
        async resendLoginTwoFactorSetupCode(challenge: string) {
            const response = await postTwoFactor('/login/two-factor/setup/resend', { challenge });

            const body = await readBody(response);

            return {
                challenge: (body?.challenge || challenge) as string,
                expiresIn: Number(body?.expiresIn) || null,
            };
        },
        /**
         * Enables the factor and returns the login body without persisting it: the recovery
         * codes have to be shown first, and they never reach the store or IndexedDB.
         */
        async confirmLoginTwoFactorSetup(challenge: string, code: string) {
            const response = await postTwoFactor('/login/two-factor/setup/confirm', { challenge, code });

            const { twoFactor, ...login } = await readBody(response) ?? {};
            if (!login.accessToken) {
                throw new NetworkError();
            }

            return {
                login,
                recoveryCodes: (Array.isArray(twoFactor?.recoveryCodes) ? twoFactor.recoveryCodes : []) as string[],
            };
        },
        async completeLogin(login: any) {
            try {
                await this.persistLogin(login);
            } catch (error) {
                if (error instanceof NetworkError) throw error;
                console.error('userStore.completeLogin', error);
                throw new DatabaseError();
            }
        },
        async persistLogin(body: any) {
            if (!body?.accessToken || !body?.person) {
                throw new NetworkError();
            }

            await set('user', {
                accessToken: body.accessToken,
                id: body.id,
                name: body.person.name,
                person: body.person,
                parent: body.parent,
                serviceParent: body.serviceparent,
                userLogin: body.login,
            });

            this.accessToken = body.accessToken;
            this.id = body.id;
            this.name = body.person.name;
            this.person = body.person;
            this.parent = body.parent;
            this.serviceParent = body.serviceparent;
            this.userLogin = body.login;
        },
        async logout() {
            const session = await get('user') ?? {};
            if (this.accessToken) {
                session.accessToken = this.accessToken;
            }
            if (session.accessToken) {
                // Revokes the token on the server; local sign-out must not depend on it.
                // Plain fetch: the axios client would redirect on a 401 before IndexedDB is cleared.
                try {
                    await apiClient.post('/users/logout', {}, {
                        headers: sessionHeaders(session),
                        timeoutMs: LOGOUT_TIMEOUT_MS,
                    });
                } catch {
                    // offline or already revoked
                }
            }

            this.$reset();
            await del('user');
        },
        async getBoss() {
            let result = null
            await api.person.getBoss().then(({ data }) => {
                this.boss = data
                result = data
            })

            return result
        }
    },
});

interface GatewayErrorDetails {
    message: string | null
    reason: string | null
    twoFactor: any
}

async function getGatewayError(response: Response): Promise<GatewayErrorDetails> {
    try {
        const error = (await response.json())?.error;
        const message = error?.message;

        return {
            message: typeof message === 'string' && message.trim() ? message : null,
            reason: typeof error?.reason === 'string' ? error.reason : null,
            twoFactor: error?.twoFactor ?? null,
        };
    } catch {
        return { message: null, reason: null, twoFactor: null };
    }
}

/** Posts to a public two-factor endpoint and turns every refusal into a typed error. */
async function postTwoFactor(path: string, data: Record<string, unknown>): Promise<Response> {
    let response: Response;
    try {
        response = await apiClient.post(path, data);
    } catch {
        throw new NetworkError();
    }
    if (response.status === 200) {
        return response;
    }

    const { message, reason, twoFactor } = await getGatewayError(response);
    const details = { status: response.status, reason, recoveryCodes: twoFactor?.recoveryCodes };
    // 410: the challenge is gone. 401/403: the code was right but the account may not log in,
    // so the challenge is used up as well.
    if (response.status === 410) {
        throw new TwoFactorChallengeExpiredError(message ?? '', details);
    }
    if (response.status === 401 || response.status === 403) {
        throw new TwoFactorLoginRefusedError(message ?? '', details);
    }
    if (response.status === 422) {
        throw new TwoFactorCodeError(message ?? '', details);
    }
    throw new GatewayMessageError(message ?? '', details);
}

async function readBody(response: Response): Promise<any> {
    try {
        return await response.json();
    } catch {
        throw new NetworkError();
    }
}

export class InvalidCredentialsError extends Error {
    constructor() {
        super('Invalid credentials');
    }
}

export class NetworkError extends Error {
    constructor() {
        super('Network error');
    }
}

/** Carries the gateway's Polish `error.message`, or an empty message when it sent none. */
export class GatewayMessageError extends Error {
    status: number | null;
    reason: string | null;
    /** Set when the factor was enabled but the login was then refused: the codes still have to be shown. */
    recoveryCodes: string[];

    constructor(
        message = '',
        { status = null, reason = null, recoveryCodes = [] }: {
            status?: number | null, reason?: string | null, recoveryCodes?: unknown
        } = {},
    ) {
        super(message);
        this.status = status;
        this.reason = reason;
        this.recoveryCodes = Array.isArray(recoveryCodes) ? recoveryCodes : [];
    }
}

export class DatabaseError extends Error {
    constructor() {
        super('Database error');
    }
}

export class TwoFactorCodeError extends GatewayMessageError {}

export class TwoFactorChallengeExpiredError extends GatewayMessageError {}

export class TwoFactorLoginRefusedError extends GatewayMessageError {}
